import { ActionIcon, Group, Stack, Text } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { IconPencil, IconTrash } from '@tabler/icons-react'
import { FormattedMessage, useIntl } from 'react-intl'
import { PipelineAlertTags } from '@/components/vlm/PipelineAlertTags'
import { useGetMilestoneServerList } from '@/queries/vlmQueries'
import { MAX_ITEMS_WITHOUT_PAGINATION } from '@/utils/pagination'
import {
  ACTION_TYPE_CONFIG,
  PATROL_RESERVED_TAG_CONFIG
} from '../vlmPipelineConfig'
import { PipelineFormValues } from './VlmPipelineForm'

type ActionSummaryProps = {
  form: UseFormReturnType<PipelineFormValues>
  index: number
  onEdit: () => void
  onDelete: () => void
}

export const ActionSummary = ({
  form,
  index,
  onEdit,
  onDelete
}: ActionSummaryProps) => {
  const intl = useIntl()
  const action = form.values.actions[index]
  const { icon } = ACTION_TYPE_CONFIG[action.type]

  // Only fetched from cache in practice: a milestone action can't be saved
  // without picking a server first, so the list is already loaded by then.
  const { data } = useGetMilestoneServerList({
    limit: MAX_ITEMS_WITHOUT_PAGINATION
  })

  const matchedAlertPrompts = [
    ...PATROL_RESERVED_TAG_CONFIG.filter(({ tag }) =>
      action.alert_tags.includes(tag)
    ).map(({ tag, labelId, severity }) => ({
      name: intl.formatMessage({ id: labelId }),
      alert_tag: tag,
      severity,
      description: ''
    })),
    ...form.values.alertPrompts.filter((alertPrompt) =>
      action.alert_tags.includes(alertPrompt.alert_tag)
    )
  ]

  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap">
      <Stack gap={6} style={{ minWidth: 0, flex: 1 }}>
        <Group gap="xs" wrap="wrap" align="center">
          {icon}
          <PipelineAlertTags alertPrompts={matchedAlertPrompts} />
        </Group>

        <Text size="xs" c="dimmed">
          {action.type === 'email' ? (
            <FormattedMessage
              id="vlm.pipelines.create.actions.summary.email"
              values={{ count: action.recipients.length }}
            />
          ) : (
            <FormattedMessage
              id="vlm.pipelines.create.actions.summary.milestone"
              values={{
                server:
                  data?.results.find(
                    (server) => server.id === action.milestone_server_id
                  )?.name ?? action.milestone_server_id
              }}
            />
          )}
        </Text>
      </Stack>

      <Group gap={4} wrap="nowrap">
        <ActionIcon
          variant="subtle"
          aria-label={intl.formatMessage({
            id: 'vlm.pipelines.create.actions.editButton'
          })}
          onClick={onEdit}
        >
          <IconPencil size={16} />
        </ActionIcon>

        <ActionIcon
          variant="subtle"
          color="red"
          aria-label={intl.formatMessage({
            id: 'vlm.pipelines.create.actions.deleteButton'
          })}
          onClick={onDelete}
        >
          <IconTrash size={16} />
        </ActionIcon>
      </Group>
    </Group>
  )
}
